import React, { useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { auth } from '../config/firebase';

/**
 * SSO Authentication Response Interface
 * Defines the structure of authentication data sent to main website
 */
interface SSOAuthResponse {
  authenticated: boolean;
  user: SSOUserData | null;
  token: string | null;
  timestamp: number;
}

/**
 * Simplified User Data Interface for SSO
 * Contains only essential user information shared with main website
 */
interface SSOUserData {
  id: string;
  email: string;
  displayName: string | null;
  emailVerified: boolean;
}

/**
 * SSO PostMessage Types
 * Defines the message types sent via postMessage to main website
 */
type SSOMessageType = 'SSO_STATUS' | 'SSO_ERROR';

interface SSOMessage {
  type: SSOMessageType;
  data?: SSOAuthResponse;
  error?: string;
  timestamp: number;
}

/**
 * SSO Check Page Component
 * 
 * This component serves as an invisible iframe endpoint for the main website
 * to check if a user is authenticated on the platform. It communicates with
 * the parent window via postMessage API.
 * 
 * Security Features:
 * - Origin validation (only sends data to onlinejustice.com)
 * - Minimal user data exposure
 * - Error handling and timeout protection
 * 
 * Usage:
 * - Accessed via iframe from main website: client.onlinejustice.com/sso-check
 * - Automatically sends authentication status to parent window
 * - Used for seamless cross-domain authentication
 */
const SSOCheckPage: React.FC = () => {
  const { user, loading } = useAuth();

  /**
   * Main website domain for postMessage security
   * Only this domain will receive authentication data
   */
  const MAIN_WEBSITE_ORIGIN = 'https://www.onlinejustice.com';

  useEffect(() => {
    /**
     * Send authentication status to parent window (main website)
     * Runs only after Firebase auth state is determined
     */
    const sendAuthStatus = async (): Promise<void> => {
      try {
        // Initialize response with unauthenticated state
        let response: SSOAuthResponse = { 
          authenticated: false, 
          user: null, 
          token: null,
          timestamp: Date.now()
        };
  
        // If user is authenticated, prepare user data and token
        if (user && auth.currentUser) {
          console.log('SSO: User authenticated, preparing response');
          
          // Get fresh Firebase ID token
          const token = await auth.currentUser.getIdToken();
          
          // Prepare minimal user data for main website
          const ssoUserData: SSOUserData = {
            id: user.id || user.uid || '',
            email: user.email || '',
            displayName: user.displayName || null,
            emailVerified: user.emailVerified || false,
          };

          response = {
            authenticated: true,
            user: ssoUserData,
            token,
            timestamp: Date.now()
          };
        } else {
          console.log('SSO: User not authenticated');
        }
        
        // Send response to main website via postMessage
        const message: SSOMessage = {
          type: 'SSO_STATUS',
          data: response,
          timestamp: Date.now()
        };

        window.parent.postMessage(message, MAIN_WEBSITE_ORIGIN);
        console.log('SSO: Authentication status sent to main website');
        
      } catch (error) {
        console.error('SSO Check error:', error);
        
        // Send error message to main website
        const errorMessage: SSOMessage = {
          type: 'SSO_ERROR',
          error: error instanceof Error ? error.message : 'Authentication check failed',
          timestamp: Date.now()
        };

        window.parent.postMessage(errorMessage, MAIN_WEBSITE_ORIGIN);
      }
    };
    
    // Only send status after Firebase auth loading is complete
    if (!loading) {
      sendAuthStatus();
    }
  }, [user, loading]);
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
        backgroundColor: 'background.default'
      }}
    >
      {/* Loading indicator */}
      <CircularProgress 
        size={32} 
        sx={{ mb: 3 }} 
        color="primary"
      />
      
      {/* Status message */}
      <Typography 
        variant="h6" 
        color="text.primary"
        sx={{ mb: 1, textAlign: 'center' }}
      >
        Checking Authentication Status
      </Typography>
      
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{ textAlign: 'center', maxWidth: 400 }}
      >
        Please wait while we verify your authentication status...
      </Typography>
      
      {/* Development info */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Development Info:</strong>
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            Auth Loading: {loading ? '⏳ Loading...' : '✅ Complete'}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            User Status: {user ? `✅ Authenticated (${user.email})` : '❌ Not authenticated'}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            Target Origin: {MAIN_WEBSITE_ORIGIN}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SSOCheckPage;